import { useState } from 'react';
import { useAdminLogs } from '../../api/hooks';
import { formatDateTime } from '../../lib/format';
import { EmptyState, Pager, Pill, Select, Spinner, cx } from '../../components/ui';

const TABS = [
  { id: 'login', label: 'Sign-ins' },
  { id: 'unauthenticated', label: 'Refused' },
  { id: 'authenticated', label: 'Activity' },
  { id: 'system', label: 'System' },
] as const;

const OUTCOMES = [
  { value: '', label: 'All outcomes' },
  { value: 'failure', label: 'Failures' },
  { value: 'success', label: 'Successes' },
];

export function AdminLogs() {
  const [category, setCategory] = useState<string>('login');
  // Sign-ins default to failures, which is what you are almost always here for.
  const [outcome, setOutcome] = useState('failure');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useAdminLogs({ category, outcome: outcome || undefined, page });

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-line bg-surface px-3 py-3 sm:px-4">
        <h1 className="text-lg font-semibold">Activity log</h1>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1 rounded-lg bg-surface-2 p-1" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={category === tab.id}
                onClick={() => {
                  setCategory(tab.id);
                  setOutcome(tab.id === 'login' ? 'failure' : '');
                  setPage(1);
                }}
                className={cx(
                  'rounded-md px-3 py-1.5 text-sm',
                  category === tab.id ? 'bg-surface font-medium shadow-sm' : 'text-ink-dim',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <Select
            ariaLabel="Outcome"
            value={outcome}
            onChange={(next) => {
              setOutcome(next);
              setPage(1);
            }}
            options={OUTCOMES}
          />
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : data?.items.length === 0 ? (
        <EmptyState title="Nothing logged here yet" />
      ) : (
        <ul className="flex-1 divide-y divide-line">
          {data?.items.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-2 sm:px-4"
              >
                <Pill tone={entry.outcome === 'failure' ? 'warn' : 'dim'}>{entry.outcome}</Pill>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">
                    {String(entry.data.action ?? entry.data.path ?? entry.category)}
                    {entry.username ? ` · ${entry.username}` : ''}
                  </p>
                  <p className="truncate text-xs text-ink-dim">
                    {formatDateTime(entry.at)}
                    {entry.data.ip ? ` · ${String(entry.data.ip)}` : ''}
                    {entry.data.status ? ` · ${String(entry.data.status)}` : ''}
                  </p>
                </div>
              </button>
              {expanded === entry.id ? (
                <pre className="overflow-x-auto border-t border-line bg-surface-2 px-4 py-3 text-xs">
                  {JSON.stringify(entry.data, null, 2)}
                </pre>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {data ? (
        <Pager page={data.page} total={data.total} pageSize={data.pageSize} onPage={setPage} />
      ) : null}
    </div>
  );
}
